import type { Agent } from "./use_agent";
import type { AgentMessage } from "../types/ai_agent";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ChatInterrupt,
  ChatMessage,
  ChatTool,
  TimelineItem,
  TimelineItemKind,
  TimelineItemTool,
  TimelineOrder,
} from "../types/chat";

function isTimelineItemKind(x: unknown): x is TimelineItemKind {
  return x === "tool" || x === "message" || x === "interrupt";
}

function normalizeMessage(message: Partial<ChatMessage>): ChatMessage {
  if (!message.id) {
    message.id = crypto.randomUUID();
  }

  if (!message.created_at) {
    message.created_at = new Date().toISOString();
  }

  return message as ChatMessage;
}

function normalizeTool(tool: Partial<ChatTool>): ChatTool {
  if (!tool.id) {
    tool.id = crypto.randomUUID();
  }

  if (!tool.kind) {
    tool.kind = "tool_activity";
  }

  if (!tool.name) {
    tool.name = "tool";
  }

  if (!tool.status) {
    tool.status = "done";
  }

  if (!tool.started_at) {
    tool.started_at = new Date().toISOString();
  }

  if (!tool.events) {
    tool.events = [];
  }

  return tool as ChatTool;
}

function normalizeInterrupt(interrupt: Partial<ChatInterrupt>): ChatInterrupt {
  if (!interrupt.id) {
    interrupt.id = crypto.randomUUID();
  }

  if (!interrupt.created_at) {
    interrupt.created_at = new Date().toISOString();
  }

  if (!interrupt.interrupt) {
    interrupt.interrupt = { action_requests: [], review_configs: [] };
  }

  return interrupt as ChatInterrupt;
}

function extractToolFailureReason(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;

  const output = (data as Record<string, unknown>).output;

  if (typeof output === "string") return output;

  if (output && typeof output === "object") {
    const record = output as Record<string, unknown>;
    const content = record.content;

    if (typeof content === "string" && content.length > 0) return content;

    const message = record.message;

    if (typeof message === "string" && message.length > 0) return message;

    const error = record.error;

    if (typeof error === "string" && error.length > 0) return error;
  }

  return null;
}

function unwrapToolOutput(output: unknown): unknown {
  if (!output || typeof output !== "object") return output ?? null;

  const record = output as Record<string, unknown>;

  if ("structured_content" in record) {
    const structured = record.structured_content;
    if (structured && typeof structured === "object") return structured;
  }

  return output;
}

function isActiveInterrupt(interrupt: ChatInterrupt) {
  const status = interrupt.metadata?.status;

  if (typeof status !== "string" || status.length === 0) {
    return true;
  }

  // Inactive statuses — accept both bare verb ("reject") and past tense ("rejected")
  if (/^reject(ed)?$/i.test(status)) return false;
  if (/^approv(e|ed)$/i.test(status)) return false;
  if (/^expir(e|ed)$/i.test(status)) return false;

  return /pending|waiting|requires_action|interrupted/i.test(status);
}


function normalizeTimelineOrder(order: TimelineOrder): TimelineOrder {
  if (!Array.isArray(order) || order.length !== 3) {
    throw new Error(
      'useChat: getTimeline({ order }) expects an array of 3 items: ["tool","message","interrupt"] in any order.',
    );
  }

  const [a, b, c] = order as unknown as [unknown, unknown, unknown];

  if (
    !isTimelineItemKind(a) || !isTimelineItemKind(b) || !isTimelineItemKind(c)
  ) {
    throw new Error(
      'useChat: getTimeline({ order }) items must be only "tool", "message", or "interrupt".',
    );
  }

  const uniq = new Set([a, b, c]);

  if (uniq.size !== 3) {
    throw new Error(
      "useChat: getTimeline({ order }) must contain 3 different values (no duplicates).",
    );
  }

  return [a, b, c];
}

export type GetTimelineOptions = {
  /**
   * - "chronological" (default): strictly by created_at ascending
   * - Tuple: force kind ordering first, then created_at within each kind
   */
  order?: "chronological" | TimelineOrder;
};

export type UseChatProps = {
  /**
   * Initial chat messages. Only visual, will not be sent to the backend.
   */
  messages?: ChatMessage[];
};

export function useChat(agent: Agent, opts: UseChatProps = {}) {
  const [isLocked, setIsLocked] = useState(false);
  const [tools, setTools] = useState<ChatTool[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>(
    opts.messages?.map(normalizeMessage) ?? [],
  );
  const [interrupts, setInterrupts] = useState<ChatInterrupt[]>([]);
  const [hitlStatus, setHitlStatus] = useState<"pending" | "submitting">(
    "pending",
  );

  const hitlStatusRef = useRef(hitlStatus);
  const toolsRef = useRef(tools);

  hitlStatusRef.current = hitlStatus;
  toolsRef.current = tools;

  const clearHitlDecisionOnStreamEndRef = useRef(false);
  const streamingAssistantIdRef = useRef<string | null>(null);
  const captureAssistantRef = useRef(false);
  const unlockAfterStreamEndRef = useRef(false);

  const stripAssistantKind = useCallback((metadata: any) => {
    if (!metadata || typeof metadata !== "object") {
      return undefined;
    }

    const { kind, ...rest } = metadata as any;

    return Object.keys(rest).length > 0 ? rest : undefined;
  }, []);

  const addMessage = useCallback((message: Partial<ChatMessage>) => {
    setMessages((prev) => [...prev, normalizeMessage(message)]);
  }, [normalizeMessage]);

  const addMessages = useCallback((messages: Partial<ChatMessage>[]) => {
    setMessages((prev) => [...prev, ...messages.map(normalizeMessage)]);
  }, [normalizeMessage]);

  const prependMessages = useCallback((messages: Partial<ChatMessage>[]) => {
    setMessages((prev) => [...messages.map(normalizeMessage), ...prev])
  }, [normalizeMessage]);

  const addTools = useCallback((tools: Partial<ChatTool>[]) => {
    setTools((prev) => [...prev, ...tools.map(normalizeTool)])
  }, []);

  const addInterrupts = useCallback((interrupts: Partial<ChatInterrupt>[]) => {
    setInterrupts((prev) => [...prev, ...interrupts.map(normalizeInterrupt)])
  }, []);

  const prependTools = useCallback((tools: Partial<ChatTool>[]) => {
    setTools((prev) => [...tools.map(normalizeTool), ...prev])
  }, []);

  const prependInterrupts = useCallback((interrupts: Partial<ChatInterrupt>[]) => {
    setInterrupts((prev) => [...interrupts.map(normalizeInterrupt), ...prev])
  }, []);

  const setAllTools = useCallback((tools: Partial<ChatTool>[]) => {
    setTools(tools.map(normalizeTool))
  }, []);

  const setAllInterrupts = useCallback((interrupts: Partial<ChatInterrupt>[]) => {
    const normalized = interrupts.map(normalizeInterrupt);

    setInterrupts(normalized);
    setIsLocked(normalized.some(isActiveInterrupt));
  }, []);

  const setAllMessages = useCallback((messages: Partial<ChatMessage>[]) => {
    setMessages(messages.map(normalizeMessage));
  }, []);

  const updateMessage = useCallback(
    (id: string, patch: Partial<ChatMessage>) => {
      setMessages((prev) =>
        prev.map((
          message,
        ) => (message.id === id ? { ...message, ...patch } : message))
      );
    },
    [],
  );

  const updateMessageMetadata = useCallback(
    (id: string, patch: any) =>
      setMessages((prev) =>
        prev.map((
          message,
        ) => (message.id === id
          ? {
            ...message,
            metadata: { ...(message.metadata ?? {}), ...(patch ?? {}) },
          }
          : message)
        )
      ),
    [],
  );

  const clear = useCallback(() => {
    setTools([]);
    setMessages([]);
    setInterrupts([]);
    setIsLocked(false);
    setHitlStatus("pending");

    captureAssistantRef.current = false;
    streamingAssistantIdRef.current = null;
    unlockAfterStreamEndRef.current = false;
  }, []);

  const markHitlSubmitting = useCallback(() => {
    setHitlStatus("submitting");
  }, []);

  /**
   * Chamado quando o countdown de um interrupt zera no frontend.
   * O backend vai automaticamente rejeitar a decisão; aqui apenas
   * marcamos o interrupt como "expired" na UI e desbloqueamos o chat
   * para o usuário poder continuar interagindo.
   */
  const expireInterrupt = useCallback((id: string) => {
    setInterrupts((prev) =>
      prev.map((interrupt) =>
        interrupt.id !== id
          ? interrupt
          : {
              ...interrupt,
              metadata: {
                ...(interrupt.metadata ?? {}),
                kind: "agent_interrupt" as const,
                status: "expired",
              },
            },
      ),
    );

    setIsLocked(false);
    setHitlStatus("pending");
  }, []);

  const beginAssistantDraft = useCallback(() => {
    const id = crypto.randomUUID();
    const created_at = new Date().toISOString();

    streamingAssistantIdRef.current = id;
    captureAssistantRef.current = true;

    setMessages((prev) => [
      ...prev,
      {
        id,
        role: "assistant",
        created_at,
        content: "",
        metadata: { kind: "assistant_draft", tool_group_start_at: created_at },
      },
    ]);
  }, []);

  const beginAssistantWaitingDecision = useCallback(() => {
    const existingId = streamingAssistantIdRef.current;

    if (existingId) {
      captureAssistantRef.current = false;

      setMessages((prev) =>
        prev.map((message) => {
          if (message.id !== existingId) {
            return message;
          }

          const created_at = message.created_at ?? new Date().toISOString();
          const tool_group_start_at = message.metadata?.tool_group_start_at ??
            created_at;

          return {
            ...message,
            role: "assistant",
            content: "",
            created_at,
            metadata: {
              kind: "assistant_waiting_decision",
              tool_group_start_at,
            },
          };
        })
      );

      return existingId;
    }

    const id = crypto.randomUUID();
    const created_at = new Date().toISOString();

    streamingAssistantIdRef.current = id;
    captureAssistantRef.current = false;

    setMessages((prev) => [
      ...prev,
      {
        id,
        role: "assistant",
        created_at,
        content: "",
        metadata: {
          kind: "assistant_waiting_decision",
          tool_group_start_at: created_at,
        },
      },
    ]);

    return id;
  }, []);

  const resumeAssistantDraft = useCallback(() => {
    const existingId = streamingAssistantIdRef.current;

    if (existingId) {
      captureAssistantRef.current = true;

      setMessages((prev) =>
        prev.map((message) => {
          if (message.id !== existingId) {
            return message;
          }

          const created_at = message.created_at ?? new Date().toISOString();
          const tool_group_start_at = message.metadata?.tool_group_start_at ??
            created_at;

          return {
            ...message,
            role: "assistant",
            content: "",
            created_at,
            metadata: { kind: "assistant_draft", tool_group_start_at },
          };
        })
      );

      return existingId;
    }

    beginAssistantDraft();

    return streamingAssistantIdRef.current;
  }, [beginAssistantDraft]);

  const disableAssistantCapture = useCallback(() => {
    streamingAssistantIdRef.current = null;
    captureAssistantRef.current = false;
  }, []);

  const unlockOnNextStreamEnd = useCallback(() => {
    unlockAfterStreamEndRef.current = true;
  }, []);

  const sendMessage = useCallback(
    async (input: ChatMessage) => {
      const text = (input.content ?? "").trim();

      if (!text || isLocked || agent.isRunning) {
        return null;
      }

      const message = {
        id: input.id ?? crypto.randomUUID(),
        role: input.role,
        content: text,
        tool_call_id: input.tool_call_id,
        created_at: input.created_at ?? new Date().toISOString(),
      };

      addMessage(message);

      const shouldCaptureAssistant = input.role === "user" &&
        !!agent.state?.agent_enabled;

      if (shouldCaptureAssistant) {
        beginAssistantDraft();
      } else {
        disableAssistantCapture();
      }

      const backendMsg: AgentMessage = {
        id: message.id,
        role: input.role,
        content: text,
        tool_call_id: input.tool_call_id,
      };

      await agent.runAgent({ messages: [backendMsg] });

      return message;
    },
    [addMessage, agent, beginAssistantDraft, disableAssistantCapture, isLocked],
  );

  const sendHitlDecision = useCallback(
    async (
      input: { name?: string; decision: "approve" | "reject" | "edit" },
    ) => {
      if (
        !isLocked || interrupts.length === 0 ||
        agent.isRunning && hitlStatus !== "pending"
      ) {
        return false;
      }

      if (input.name) {
        const found = interrupts.some((interrupt) =>
          interrupt.interrupt.action_requests?.some((actionRequest: any) =>
            actionRequest?.name === input.name
          )
        );

        if (!found) {
          return false;
        }
      }

      markHitlSubmitting();

      const localStatus = input.decision === "approve"
        ? "approved"
        : input.decision === "reject"
        ? "rejected"
        : undefined;

      if (localStatus) {
        setInterrupts((prev) =>
          prev.map((interrupt) => {
            const matches = !input.name ||
              interrupt.interrupt.action_requests?.some(
                (ar) => ar.name === input.name,
              );

            if (!matches) {
              return interrupt;
            }

            return {
              ...interrupt,
              metadata: {
                ...(interrupt.metadata ?? {}),
                kind: "agent_interrupt" as const,
                status: localStatus,
              },
            };
          }),
        );
      }

      const nextState = {
        ...(agent.state ?? {}),
        hitl_decision: { decisions: [{ type: input.decision }] },
      };

      agent.setState(nextState);
      clearHitlDecisionOnStreamEndRef.current = true;

      if (!!agent.state?.agent_enabled) {
        resumeAssistantDraft();
      } else {
        disableAssistantCapture();
      }

      unlockOnNextStreamEnd();

      await agent.runAgent({ messages: [], state: nextState });

      return true;
    },
    [
      agent,
      disableAssistantCapture,
      interrupts,
      hitlStatus,
      isLocked,
      markHitlSubmitting,
      resumeAssistantDraft,
      unlockOnNextStreamEnd,
    ],
  );

  const getTimeline = useCallback((timelineOpts: GetTimelineOptions = {}) => {
    const orderMode = timelineOpts.order ?? "chronological";
    const isForcedKindOrder = orderMode !== "chronological";
    const kindRank: Record<TimelineItemKind, number> = {
      tool: 0,
      message: 1,
      interrupt: 2,
    };

    const stripToolOnlyAnchors = (arr: TimelineItem[]) => {
      return arr.filter((it) => {
        if (it.kind !== "message") {
          return true;
        }

        const isToolOnly =
          it.message.role === "assistant" &&
          it.message.metadata?.kind === "assistant_tool_only" &&
          !(it.message.content ?? "").trim();

        return !isToolOnly;
      });
    };

    if (isForcedKindOrder) {
      const timelineOrder = normalizeTimelineOrder(orderMode);

      for (let i = 0; i < timelineOrder.length; i++) {
        kindRank[timelineOrder[i]] = i;
      }
    }

    const items: TimelineItem[] = [];

    for (const m of messages) {
      items.push({
        kind: "message",
        id: m.id!,
        created_at: m.created_at!,
        message: m,
      });
    }

    for (const t of tools) {
      items.push({ kind: "tool", id: t.id, created_at: t.started_at, tool: t });
    }

    for (const i of interrupts) {
      items.push({
        kind: "interrupt",
        id: i.id,
        created_at: i.created_at,
        interrupt: i.interrupt,
        metadata: i.metadata,
      });
    }

    const withIdx = items.map((x, idx) => ({ x, idx }));

    withIdx.sort((a, b) => {
      if (a.x.created_at < b.x.created_at) return -1;
      if (a.x.created_at > b.x.created_at) return 1;

      if (isForcedKindOrder) {
        const rankA = kindRank[a.x.kind];
        const rankB = kindRank[b.x.kind];

        if (rankA < rankB) return -1;
        if (rankA > rankB) return 1;
      }

      return a.idx - b.idx;
    });

    const sorted = withIdx.map(({ x }) => x);

    if (!isForcedKindOrder) {
      return stripToolOnlyAnchors(sorted);
    }

    const anchors: Array<{ id: string; startAt: string }> = [];

    for (const it of sorted) {
      if (it.kind !== "message") {
        continue;
      }

      if (it.message.role !== "assistant") {
        continue;
      }

      const startAt = it.message.metadata?.tool_group_start_at;

      if (typeof startAt === "string" && startAt.length > 0) {
        anchors.push({ id: it.id, startAt });
      }
    }

    if (anchors.length === 0) {
      return stripToolOnlyAnchors(sorted);
    }

    anchors.sort((
      a,
      b,
    ) => (a.startAt < b.startAt ? -1 : a.startAt > b.startAt ? 1 : 0));

    const bucketByAnchorId = new Map<string, TimelineItemTool[]>();

    for (const a of anchors) {
      bucketByAnchorId.set(a.id, []);
    }

    let anchorPtr = -1;

    for (const it of sorted) {
      if (it.kind !== "tool") {
        continue;
      }

      const startedAt = it.tool.started_at;

      while (
        anchorPtr + 1 < anchors.length &&
        startedAt >= anchors[anchorPtr + 1].startAt
      ) {
        anchorPtr++;
      }

      if (anchorPtr >= 0) {
        bucketByAnchorId.get(anchors[anchorPtr].id)!.push(it);
      }
    }

    const movedToolIds = new Set<string>();

    for (const [, bucket] of bucketByAnchorId) {
      for (const t of bucket) {
        movedToolIds.add(t.id);
      }
    }

    if (movedToolIds.size === 0) {
      return stripToolOnlyAnchors(sorted);
    }

    const kept = sorted.filter((it) =>
      !(it.kind === "tool" && movedToolIds.has(it.id))
    );
    const toolsBeforeMessage = kindRank.tool < kindRank.message;

    const out: TimelineItem[] = [];

    for (const it of kept) {
      if (it.kind === "message") {
        const bucket = bucketByAnchorId.get(it.id);

        if (bucket && bucket.length > 0 && toolsBeforeMessage) {
          out.push(...bucket);
        }
      }

      out.push(it);

      if (it.kind === "message") {
        const bucket = bucketByAnchorId.get(it.id);

        if (bucket && bucket.length > 0 && !toolsBeforeMessage) {
          out.push(...bucket);
        }
      }
    }

    return stripToolOnlyAnchors(out);
  }, [interrupts, messages, tools]);

  useEffect(() => {
    if (!agent.error) {
      return;
    }

    const id = streamingAssistantIdRef.current;

    if (!id) {
      return;
    }

    streamingAssistantIdRef.current = null;
    captureAssistantRef.current = false;

    setMessages((prev) =>
      prev.map((m) =>
        m.id === id
          ? {
            ...m,
            content: agent.error ?? "",
            metadata: {
              kind: "assistant_error",
              error: agent.error === null ? undefined : agent.error,
            },
          }
          : m
      )
    );
  }, [agent.error]);

  useEffect(() => {
    const offAgentStream = agent.onAgentStream((event) => {
      if (!captureAssistantRef.current) {
        return;
      }

      const token = event?.data?.token || "";

      if (!token) {
        return;
      }

      const id = streamingAssistantIdRef.current;

      if (!id) {
        return;
      }

      setMessages((prev) =>
        prev.map((
          message,
        ) => (message.id === id
          ? { ...message, content: message.content + token }
          : message)
        )
      );
    });

    const offAgentEnd = agent.onAgentEnd((event) => {
      if (!captureAssistantRef.current) {
        return;
      }

      const id = streamingAssistantIdRef.current;

      if (!id) {
        return;
      }

      const answer = event?.data?.answer || "";
      const toolCalls = event?.data?.tool_calls;
      const hasToolCalls = Array.isArray(toolCalls)
        ? toolCalls.length > 0
        : !!toolCalls;

      if (!answer && hasToolCalls) {
        return;
      }

      if (answer.length > 0) {
        setMessages((prev) =>
          prev.map((
            m,
          ) => (m.id === id
            ? {
              ...m,
              content: answer,
              metadata: stripAssistantKind(m.metadata),
            }
            : m)
          )
        );
      } else {
        setMessages((prev) =>
          prev.map((
            m,
          ) => (m.id === id
            ? { ...m, metadata: stripAssistantKind(m.metadata) }
            : m)
          )
        );
      }

      streamingAssistantIdRef.current = null;
      captureAssistantRef.current = false;
    });

    const offInterrupt = agent.onAgentInterrupt((event) => {
      setHitlStatus("pending");

      const created_at = new Date().toISOString();
      const interrupts = event?.data?.interrupts || [];

      if (interrupts.length) {
        setInterrupts((prev) => [
          ...prev,
          ...interrupts.map((interrupt) => {
            const firstRequest = interrupt.action_requests?.[0] as
              | (typeof interrupt.action_requests[0] & {
                  tool_call_id?: string;
                  tool_name?: string;
                })
              | undefined;

            return {
              id: crypto.randomUUID(),
              created_at,
              interrupt: {
                action_requests: interrupt.action_requests,
                review_configs: interrupt.review_configs,
              },
              metadata: {
                kind: "agent_interrupt" as const,
                status: "pending",
                toolCallId: firstRequest?.tool_call_id ?? null,
                originToolName: firstRequest?.tool_name ?? null,
              },
            };
          }),
        ]);
      }

      beginAssistantWaitingDecision();
      setIsLocked(true);
    });

    const offToolStart = agent.onToolStart((event) => {
      setTools((prev) => {
        const id = event.data?.id || event.data?.name;

        if (!id) {
          return prev;
        }

        const existingIdx = prev.findIndex((t) => t.id === id);

        if (existingIdx >= 0) {
          const updated = [...prev];
          updated[existingIdx] = {
            ...updated[existingIdx],
            status: "running",
            tool_start: event.data,
          };
          return updated;
        }

        return [
          ...prev,
          {
            id,
            kind: "tool_activity",
            name: event.data?.name,
            status: "running",
            started_at: new Date().toISOString(),
            tool_start: event.data,
          },
        ];
      });
    });

    const offToolEnd = agent.onToolEnd((event) => {
      setTools((prev) => {
        const eventId = event.data?.id;
        const eventName = event.data?.name;
        const statusStr = String(event.data?.status ?? "");
        const nextStatus: ChatTool["status"] = statusStr.toLowerCase() === "interrupt"
          ? "interrupt"
          : /error|fail/i.test(statusStr) ? "error" : "done";

        if (eventId) {
          const idx = prev.findIndex((t) => t.id === eventId);

          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], name: eventName ?? updated[idx].name, status: nextStatus, ended_at: new Date().toISOString(), tool_end: event.data };
            return updated;
          }
        }

        let idx = -1;

        for (let j = prev.length - 1; j >= 0; j--) {
          const tool = prev[j];

          if (tool.status !== "running") {
            continue;
          }

          if (tool.id === eventId || tool.name === eventName) {
            idx = j;

            break;
          }
        }

        if (idx === -1) {
          return [
            ...prev,
            {
              id: eventId || eventName,
              kind: "tool_activity",
              name: eventName,
              status: nextStatus,
              started_at: new Date().toISOString(),
              ended_at: new Date().toISOString(),
              tool_end: event.data,
            },
          ];
        }

        const updated = [...prev];

        updated[idx] = {
          ...updated[idx],
          status: nextStatus,
          ended_at: new Date().toISOString(),
          tool_end: event.data,
        };

        return updated;
      });

      // Registra no interrupt o desfecho real da tool (sucesso/falha) usando
      // o `tool_call_id` como chave de correlação. Não sobrescreve `status` —
      // a decisão do usuário (approved/rejected) permanece. A UI usa
      // `toolStatus` para mostrar "aprovado mas execução falhou".
      const toolCallId = event.data?.id;
      const toolStatusStr = String(event.data?.status ?? "");

      if (typeof toolCallId === "string" && toolCallId.length > 0) {
        const isFailure = /error|fail/i.test(toolStatusStr);
        const failureReason = isFailure
          ? extractToolFailureReason(event.data)
          : null;
        const toolOutput = unwrapToolOutput(event.data?.output);

        setInterrupts((prev) => {
          const idx = prev.findIndex(
            (interrupt) => interrupt.metadata?.toolCallId === toolCallId,
          );

          if (idx === -1) return prev;

          const updated = [...prev];

          updated[idx] = {
            ...updated[idx],
            metadata: {
              ...(updated[idx].metadata ?? {}),
              kind: "agent_interrupt" as const,
              toolStatus: isFailure ? "failed" : "succeeded",
              failureReason,
              toolOutput,
            },
          };

          return updated;
        });
      }
    });

    const appendToolEvent = (event: any) => {
      const eventName = event?.data?.name;

      if (!eventName) {
        return;
      }

      setTools((prev) => {
        let idx = -1;

        for (let j = prev.length - 1; j >= 0; j--) {
          if (prev[j].name === eventName) {
            idx = j;
            break;
          }
        }

        if (idx === -1) {
          return [
            ...prev,
            normalizeTool({
              id: crypto.randomUUID(),
              kind: "tool_activity",
              name: eventName,
              status: "running",
              started_at: new Date().toISOString(),
              events: [event],
            }),
          ];
        }

        const updated = [...prev];
        const existing = updated[idx];
        updated[idx] = {
          ...existing,
          events: [...(existing.events ?? []), event],
        };
        return updated;
      });
    };

    const offToolProgress = agent.onToolProgress((event) => appendToolEvent(event));
    const offToolMessage = agent.onToolMessage((event) => appendToolEvent(event));

    const offStreamEnd = agent.onStreamEnd(() => {
      setTools((prev) =>
        prev.map((
          t,
        ) => (t.status === "running"
          ? { ...t, status: "error", ended_at: new Date().toISOString() }
          : t)
        )
      );

      const draftId = streamingAssistantIdRef.current;

      if (draftId) {
        setMessages((prev) => {
          const msg = prev.find((m) => m.id === draftId);

          if (!msg || msg.metadata?.kind !== "assistant_draft") {
            return prev;
          }

          const text = (msg.content ?? "").trim();

          if (text.length === 0) {
            streamingAssistantIdRef.current = null;
            captureAssistantRef.current = false;

            const toolGroupStartAt = msg.metadata?.tool_group_start_at ?? msg.created_at ?? "";
            const hasToolActivity = toolsRef.current.some((t) => t.started_at >= toolGroupStartAt);

            // Se a execução só teve ferramentas, mantemos uma âncora invisível
            // para que o agrupamento/ordenação de tools não "salte" para a mensagem anterior.
            if (hasToolActivity) {
              return prev.map((m) =>
                m.id === draftId
                  ? {
                      ...m,
                      role: "assistant",
                      content: "",
                      metadata: {
                        ...(m.metadata ?? {}),
                        kind: "assistant_tool_only",
                      },
                    }
                  : m,
              );
            }

            return prev.filter((m) => m.id !== draftId);
          }

          streamingAssistantIdRef.current = null;
          captureAssistantRef.current = false;

          return prev.map((
            m,
          ) => (m.id === draftId
            ? { ...m, metadata: stripAssistantKind(m.metadata) }
            : m)
          );
        });
      }

      if (
        clearHitlDecisionOnStreamEndRef.current ||
        hitlStatusRef.current === "submitting"
      ) {
        clearHitlDecisionOnStreamEndRef.current = false;

        setHitlStatus("pending");
        setInterrupts((prev) =>
          prev.filter((i) =>
            /^(approv(e|ed)|reject(ed)?|expir(e|ed))$/i.test(
              i.metadata?.status ?? "",
            )
          ),
        );
        setIsLocked(false);

        if ("hitl_decision" in agent.state) {
          const { hitl_decision: _ignored, ...rest } = agent.state;

          agent.setState(rest);
        }
      }

      if (unlockAfterStreamEndRef.current) {
        unlockAfterStreamEndRef.current = false;

        setIsLocked(false);
      }
    });

    return () => {
      offAgentStream();
      offAgentEnd();
      offInterrupt();
      offToolStart();
      offToolEnd();
      offToolProgress();
      offToolMessage();
      offStreamEnd();
    };
  }, [agent]);

  return {
    messages,
    tools,
    isLocked,
    interrupts,
    hitlStatus,
    setMessages: setAllMessages,
    setTools: setAllTools,
    setInterrupts: setAllInterrupts,
    addMessage,
    addMessages,
    prependMessages,
    addTools,
    addInterrupts,
    prependTools,
    prependInterrupts,
    sendMessage,
    sendHitlDecision,
    updateMessage,
    updateMessageMetadata,
    markHitlSubmitting,
    expireInterrupt,
    clear,
    beginAssistantDraft,
    disableAssistantCapture,
    unlockOnNextStreamEnd,
    getTimeline,
  };
}
